import React from 'react';
import { useNavigate } from 'react-router-dom';
import { loanHeader, loanDetails } from '../mock';
import LoanPipeline from '../components/loan/LoanPipeline';
import BorrowerDetails from '../components/loan/BorrowerDetails';
import ActionHistory from '../components/loan/ActionHistory';
import MiscForms from '../components/loan/MiscForms';
import { TrendingUp, AlertCircle, CheckCircle2, Clock, ArrowUpRight, FileText, Receipt, Upload, MessagesSquare } from 'lucide-react';

export default function LoanStatus() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6 max-w-[1500px] mx-auto">
      {/* Breadcrumbs + Title */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11.5px] text-neutral-500">
            <span>Pipeline</span>
            <span className="text-neutral-300">/</span>
            <span className="mono">Loan #{loanHeader.loanNumber}</span>
            <span className="text-neutral-300">/</span>
            <span className="text-neutral-900 font-semibold">Loan Status</span>
          </div>
          <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">
            {loanHeader.borrowerShort.split(',')[0].charAt(0) + loanHeader.borrowerShort.split(',')[0].slice(1).toLowerCase()} File
          </h1>
          <p className="text-[13px] text-neutral-500 mt-1">
            Real‑time view · {loanDetails.loanPurpose} · <span className="mono font-semibold text-neutral-700">{loanHeader.status}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/loans/126096012900/summary')} className="h-9 px-3 rounded-md border hairline bg-white hover:border-neutral-400 text-[11.5px] font-semibold text-neutral-700 flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Summary
          </button>
          <button onClick={() => navigate('/loans/126096012900/fees')} className="h-9 px-3 rounded-md border hairline bg-white hover:border-neutral-400 text-[11.5px] font-semibold text-neutral-700 flex items-center gap-1.5">
            <Receipt className="h-3.5 w-3.5" /> Fees
          </button>
          <button onClick={() => navigate('/loans/126096012900/documents')} className="h-9 px-3 rounded-md border hairline bg-white hover:border-neutral-400 text-[11.5px] font-semibold text-neutral-700 flex items-center gap-1.5">
            <Upload className="h-3.5 w-3.5" /> Documents
          </button>
          <button onClick={() => navigate('/loans/126096012900/messages')} className="h-9 px-3 rounded-md border hairline bg-white hover:border-neutral-400 text-[11.5px] font-semibold text-neutral-700 flex items-center gap-1.5">
            <MessagesSquare className="h-3.5 w-3.5" /> Log
          </button>
          <button className="h-9 px-3 rounded-md btn-ink text-[11.5px] font-semibold flex items-center gap-1.5">
            <ArrowUpRight className="h-3.5 w-3.5" /> Request docs
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <HeaderStat label="Front" value={`${loanHeader.front}%`} hint="Housing ratio" />
        <HeaderStat label="Back" value={`${loanHeader.back}%`} hint="Total ratio" />
        <HeaderStat label="LTV" value={`${loanHeader.ltv}%`} hint="Loan-to-value" />
        <HeaderStat label="CLTV" value={`${loanHeader.cltv}%`} hint="Combined" />
        <HeaderStat label="Base LTV" value={`${loanHeader.baseLtv}%`} hint="Pre-MI" />
        <HeaderStat label="Rate" value={`${loanHeader.rate}%`} hint="Locked" accent />
        <HeaderStat label="APR" value={`${loanDetails.apr}%`} hint="All-in" accent />
      </div>

      {/* Insight banner */}
      <div className="relative overflow-hidden rounded-2xl border hairline bg-neutral-900 text-white p-5 noise">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[hsl(var(--kf-gold))]/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-5 justify-between">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-[hsl(var(--kf-gold))] text-neutral-900 flex items-center justify-center border hairline">
              <TrendingUp className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold))] font-bold">Fast track eligible</div>
              <div className="display text-[17px] font-bold text-white">
                This loan is 4 business days ahead of cohort average for Non‑QM refis.
              </div>
              <div className="text-[12px] text-white/60 mt-0.5">Recommended next: request closing documents before EOD · saves ~18h cycle time.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <MiniMetric icon={Clock} label="Cycle" value="19d" sub="vs 23d avg" />
            <MiniMetric icon={CheckCircle2} label="Cleared" value="11/14" sub="conditions" />
            <MiniMetric icon={AlertCircle} label="Open" value="3" sub="borrower-side" tone="gold" />
          </div>
        </div>
      </div>

      <LoanPipeline />
      <BorrowerDetails />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          <ActionHistory />
        </div>
        <MiscForms />
      </div>
    </div>
  );
}

function HeaderStat({ label, value, hint, accent }) {
  return (
    <div className="rounded-xl border hairline bg-white p-3.5 card-lift relative overflow-hidden">
      {accent && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[hsl(var(--kf-gold))]" />}
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{label}</div>
      <div className="display text-[22px] font-bold tracking-tight mt-0.5 text-neutral-900">{value}</div>
      <div className="text-[10.5px] text-neutral-500 mt-0.5">{hint}</div>
    </div>
  );
}

function MiniMetric({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur px-3 py-2 min-w-[120px]">
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-wider text-white/50 font-bold">
        <Icon className={`h-3 w-3 ${tone === 'gold' ? 'text-[hsl(var(--kf-gold))]' : 'text-white/70'}`} /> {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className="display text-[16px] font-bold text-white">{value}</span>
        <span className="text-[10px] text-white/50">{sub}</span>
      </div>
    </div>
  );
}
